/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { storeLocalImageActions } from '../../redux';
import { NIImageData } from '../components/form/services/form';
import { Dispatch } from "redux";
import { from, Observable, of } from "rxjs";
import { tap } from "rxjs/operators";
import config from "../../config";
import { getDB } from "../../db/store";
import { IMAGE_STORE, OFFLINE_IMAGE_UPDATE_STORE } from "../../db/stores/images";
import { loadToken } from "../../utils/token";

export interface ImageService {
  getImageData: (ids: string[]) => Observable<NIImageData[]>;
  downloadImage: (id: string) => Observable<NIImageData>;
}

export class ImageServiceImpl implements ImageService {
  private readonly dispatch: Dispatch;

  constructor(dispatch: Dispatch) {
    this.dispatch = dispatch;
  }

  getImageData = (ids: string[]): Observable<NIImageData[]> => {
    if (!ids || ids.length === 0) {
      return of([]);
    }

    return from(
      getDB().then(db => {
        const tx = db.transaction(
          [IMAGE_STORE, OFFLINE_IMAGE_UPDATE_STORE],
          "readonly"
        );
        const offlineStore = tx.objectStore(OFFLINE_IMAGE_UPDATE_STORE);
        const imgStore = tx.objectStore(IMAGE_STORE);
        return Promise.all(
          ids.filter(id => id !== undefined).map((id: string) =>
            offlineStore.get(id).then(offlineData => {
              if (offlineData !== undefined) {
                return Promise.resolve({
                  id,
                  uri: offlineData.uri
                });
              }

              return imgStore.get(id).then(imgData => {
                return Promise.resolve({
                  id,
                  uri: imgData !== undefined ? imgData.uri : null
                });
              })
            })
          )
        );
      })
    );
  }

  downloadImage = (id: string): Observable<NIImageData> => {
    const token = loadToken();
    return from(new Promise<NIImageData>(function (resolve, reject) {
      return fetch(`${config.dfdmHost}/api/attachments/${id}/data?size=full`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token!.access_token}`,
        }
      }).then(response => {
        if (response.status !== 200) {
          console.log(response);
          throw new Error(JSON.stringify({
            status: response.status,
            text: response.statusText,
            type: response.type,
            body: response.body
          }));
        }
        return response.blob();
      }).then(blob => {
        const reader = new FileReader();
        reader.onloadend = () => resolve({ id, uri: reader.result as string });
        reader.readAsDataURL(blob);
      })
        .catch(error => reject(error));
    })).pipe(
      tap((data) => {
        if (data.uri) {
          this.dispatch(storeLocalImageActions.request({ id: data.id, uri: data.uri }));
        }
      })
    );
  };
}
